import type { CacheBucket } from "@elaso/shared/schemas/platform";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ulid } from "ulid";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import {
	ALL_SCOPES,
	type CacheScopes,
	chooseOrganizationScope,
	chooseWorkspaceScope,
	clearCacheData,
	SCOPE_ALL,
	scopedId,
} from "@/lib/cache-scope";
import {
	type InstanceOrganizationRow,
	organizationKindLabel,
} from "@/lib/instance-organization";
import { ws } from "@/lib/websocket-wrapper";

type WorkspaceOption = { id: string; name: string };

export const Route = createFileRoute("/instance/diagnostics")({
	component: InstanceDiagnosticsPage,
});

/**
 * `/instance/diagnostics`: the server's caches and its CPU profile.
 * Engineering tools, kept out of the everyday flow on a page of their own and
 * behind the same admission as the rest of the client, which the layout route
 * (`instance/route.tsx`) decides. They stay in the ELASO Instance Admin Client
 * while the Shared Instance is the only ELASO Instance.
 */
function InstanceDiagnosticsPage() {
	return (
		<div className="flex flex-col items-center gap-4 p-4 md:p-8">
			<CacheControlsSection />
			<CpuProfileSection />
		</div>
	);
}

type CacheBucketOption = { id: "all" | CacheBucket; label: string };

const CACHE_BUCKET_OPTIONS: CacheBucketOption[] = [
	{ id: "all", label: "All caches" },
	{ id: "userRoles", label: "User roles" },
	{ id: "assetPermissions", label: "Asset permissions" },
	{ id: "workspaceParticipants", label: "Workspace participants" },
];

// Which kind of target input a bucket takes (empty = clear the whole bucket).
function targetModeFor(
	bucket: "all" | CacheBucket
): "none" | "workspace" | "user" | "asset" {
	switch (bucket) {
		case "all":
			return "none";
		case "workspaceParticipants":
			return "workspace";
		case "assetPermissions":
			return "asset";
		default:
			return "user"; // userRoles, email or firebase uid
	}
}

type WarmCacheResponse = {
	workspaces: number;
	entriesWarmed: number;
	failedWorkspaces: number;
	durationMs: number;
	bucket: "all" | CacheBucket;
	workspaceId?: string;
	organizationId?: string;
};

// A full warm re-enumerates every workspace against OpenFGA, so it can far
// outlive the WS wrapper's 30s default.
const WARM_RPC_TIMEOUT_MS = 10 * 60 * 1000;

/** Confirm-dialog copy for the clear button, by precedence: target → Organisation scope → workspace scope → whole bucket. */
function clearConfirmDescription({
	bucket,
	bucketLabel,
	trimmedTarget,
	scopedWorkspaceName,
	scopedOrganizationName,
}: {
	bucket: "all" | CacheBucket;
	bucketLabel: string;
	trimmedTarget: string;
	scopedWorkspaceName: string | undefined;
	scopedOrganizationName: string | undefined;
}): string {
	if (trimmedTarget && bucket !== "all") {
		return `This clears the "${bucketLabel}" entry for "${trimmedTarget}".`;
	}
	if (scopedOrganizationName) {
		return bucket === "all"
			? `This clears every server cache entry belonging to Organisation "${scopedOrganizationName}", across every workspace it owns, in one operation. Other Organisations stay warm; cleared entries self-heal on the next read.`
			: `This clears the "${bucketLabel}" cache for Organisation "${scopedOrganizationName}", across every workspace it owns. Other Organisations stay warm.`;
	}
	if (scopedWorkspaceName) {
		return bucket === "all"
			? `This clears every server cache entry belonging to workspace "${scopedWorkspaceName}". Other workspaces stay warm; cleared entries self-heal on the next read.`
			: `This clears the "${bucketLabel}" cache for workspace "${scopedWorkspaceName}". Other workspaces stay warm.`;
	}
	if (bucket === "all") {
		return "This clears EVERY server cache (user roles, asset permissions, workspace participants, user profiles). Entries are authoritative and self-heal on the next read, but this forces a cold re-fetch from OpenFGA/Strapi.";
	}
	return `This clears the entire "${bucketLabel}" cache.`;
}

/**
 * Toast suffix for a warm result: the bucket, then the scope the server
 * confirms it warmed. Read from the response ids, not the current selection,
 * so the message stays right if the selects change while the warm runs.
 */
function warmedScopeLabel(
	result: WarmCacheResponse,
	lists: {
		workspaces: WorkspaceOption[];
		organizations: InstanceOrganizationRow[];
	}
): string {
	const bucket =
		result.bucket === "all"
			? ""
			: ` (${CACHE_BUCKET_OPTIONS.find((o) => o.id === result.bucket)?.label ?? result.bucket})`;
	if (result.organizationId) {
		const name =
			organizationOptionLabel(
				lists.organizations.find((o) => o.id === result.organizationId)
			) ?? result.organizationId;
		return `${bucket} for Organisation ${name}`;
	}
	if (result.workspaceId) {
		const name =
			lists.workspaces.find((w) => w.id === result.workspaceId)?.name ??
			result.workspaceId;
		return `${bucket} for workspace ${name}`;
	}
	return bucket;
}

/** Toast suffix for a clear result, by precedence: target → Organisation → workspace. */
function clearedScopeLabel(
	result: { target?: string; workspaceId?: string; organizationId?: string },
	names: { workspace?: string; organization?: string }
): string {
	if (result.target) return ` (${result.target})`;
	if (result.organizationId) {
		return ` (Organisation ${names.organization ?? result.organizationId})`;
	}
	if (result.workspaceId) {
		return ` (workspace ${names.workspace ?? result.workspaceId})`;
	}
	return "";
}

/**
 * One Organisation's name in a picker. The root and its shadow share a name
 * (see `organizationKindLabel`), and a disabled Organisation is still listed
 * here because offboarding it is the case the scope exists for.
 */
function organizationOptionLabel(
	organization: InstanceOrganizationRow | undefined
): string | undefined {
	if (!organization) return;
	const kind = organizationKindLabel(organization);
	const state = organization.enabled ? "" : " (disabled)";
	return kind
		? `${organization.name} — ${kind}${state}`
		: `${organization.name}${state}`;
}

/**
 * The two cache scopes, workspace and Organisation, with the lists
 * that name them. They are exclusive on the wire (the server rejects a request
 * carrying both), so picking one resets the other.
 */
function useCacheScopes() {
	const [scopes, setScopes] = useState<CacheScopes>(ALL_SCOPES);
	const { workspace: workspaceScope, organization: organizationScope } = scopes;
	const [workspaces, setWorkspaces] = useState<WorkspaceOption[]>([]);
	const [organizations, setOrganizations] = useState<InstanceOrganizationRow[]>(
		[]
	);

	// Workspaces for the `workspaceParticipants` target dropdown and the workspace
	// scope. Non-blocking, so if it fails the rest of the controls still work.
	useEffect(() => {
		ws.sendWithPromise<{ workspaces: WorkspaceOption[] }>({
			id: ulid(),
			procedure: "instanceAdmin.listWorkspaces",
			data: {},
		})
			.then((res) => setWorkspaces(res.workspaces))
			.catch(() => {
				/* ignore, the scope and the participant target are the only consumers */
			});
	}, []);

	// Organisations for the Organisation scope. Disabled ones are listed too:
	// offboarding a disabled Organisation is exactly when its cached answers
	// are flushed. Non-blocking, like the workspace list.
	useEffect(() => {
		ws.sendWithPromise<{ organizations: InstanceOrganizationRow[] }>({
			id: ulid(),
			procedure: "instanceAdmin.listOrganizations",
			data: { includeDisabled: true },
		})
			.then((res) => setOrganizations(res.organizations))
			.catch(() => {
				/* ignore, the Organisation scope is the only consumer */
			});
	}, []);

	function setWorkspace(value: string) {
		setScopes((current) => chooseWorkspaceScope(current, value));
	}
	function setOrganization(value: string) {
		setScopes((current) => chooseOrganizationScope(current, value));
	}

	const workspaceId = scopedId(workspaceScope);
	const workspaceName = workspaceId
		? (workspaces.find((w) => w.id === workspaceId)?.name ?? workspaceId)
		: undefined;
	const organizationId = scopedId(organizationScope);
	const organizationName = organizationId
		? (organizationOptionLabel(
				organizations.find((o) => o.id === organizationId)
			) ?? organizationId)
		: undefined;

	return {
		chosen: scopes,
		workspaceScope,
		organizationScope,
		workspaces,
		organizations,
		setWorkspace,
		setOrganization,
		workspaceId,
		workspaceName,
		organizationId,
		organizationName,
	};
}

function CacheControlsSection() {
	const [bucket, setBucket] = useState<"all" | CacheBucket>("all");
	const [target, setTarget] = useState("");
	const scopes = useCacheScopes();
	const {
		workspaces,
		organizations,
		workspaceId: scopedWorkspaceId,
		workspaceName: scopedWorkspaceName,
		organizationId: scopedOrganizationId,
		organizationName: scopedOrganizationName,
	} = scopes;
	const [submitting, setSubmitting] = useState(false);
	const [warming, setWarming] = useState(false);
	const [confirmOpen, setConfirmOpen] = useState(false);

	// Switching bucket clears any stale target from the previous bucket.
	// biome-ignore lint/correctness/useExhaustiveDependencies: reset target on bucket change
	useEffect(() => {
		setTarget("");
	}, [bucket]);

	const targetMode = targetModeFor(bucket);
	const trimmedTarget = target.trim();

	async function handleConfirm() {
		setConfirmOpen(false);
		setSubmitting(true);
		try {
			const result = await ws.sendWithPromise<{
				cleared: string[];
				target?: string;
				workspaceId?: string;
				organizationId?: string;
			}>({
				id: ulid(),
				procedure: "instanceAdmin.clearCache",
				data: clearCacheData(bucket, target, scopes.chosen),
			});
			const scope = clearedScopeLabel(result, {
				workspace: scopedWorkspaceName,
				organization: scopedOrganizationName,
			});
			toast.success(`Cleared: ${result.cleared.join(", ")}${scope}`);
			setTarget("");
		} catch (err) {
			const message = err instanceof Error ? err.message : String(err);
			toast.error(`Clear failed: ${message}`);
		} finally {
			setSubmitting(false);
		}
	}

	async function handleWarm() {
		setWarming(true);
		try {
			const result = await ws.sendWithPromise<WarmCacheResponse>({
				id: ulid(),
				procedure: "instanceAdmin.warmCache",
				data: {
					bucket,
					workspaceId: scopedWorkspaceId,
					organizationId: scopedOrganizationId,
				},
				timeoutMs: WARM_RPC_TIMEOUT_MS,
			});
			const seconds = (result.durationMs / 1000).toFixed(1);
			const scopeLabel = warmedScopeLabel(result, {
				workspaces,
				organizations,
			});
			const summary = `Warmed ${result.entriesWarmed} entries across ${result.workspaces} workspace(s)${scopeLabel} in ${seconds}s`;
			if (result.failedWorkspaces > 0) {
				toast.warning(
					`${summary} — ${result.failedWorkspaces} workspace warm(s) failed; those keys stay cold and self-heal on read`
				);
			} else {
				toast.success(summary);
			}
		} catch (err) {
			const message = err instanceof Error ? err.message : String(err);
			toast.error(`Warm failed: ${message}`);
		} finally {
			setWarming(false);
		}
	}

	const bucketLabel =
		CACHE_BUCKET_OPTIONS.find((o) => o.id === bucket)?.label ?? bucket;
	const confirmDescription = clearConfirmDescription({
		bucket,
		bucketLabel,
		trimmedTarget,
		scopedWorkspaceName,
		scopedOrganizationName,
	});

	return (
		<div className="flex w-full justify-center">
			<Card className="w-full max-w-lg">
				<CardHeader>
					<CardTitle>
						<h2>Cache controls</h2>
					</CardTitle>
					<CardDescription>
						Internal — clear server caches completely, by bucket, by
						Organisation, by workspace, or a single targeted entry. Cleared
						entries self-heal on the next read. Warming repopulates caches up
						front (same pass as server start) — all of them, one bucket, one
						Organisation, or one workspace — so reads don't pay the cold-miss
						cost.
					</CardDescription>
				</CardHeader>
				<CardContent>
					<div className="space-y-4">
						<div className="space-y-2">
							<Label htmlFor="cache-bucket">Bucket</Label>
							<Select
								onValueChange={(v) => setBucket(v as "all" | CacheBucket)}
								value={bucket}
							>
								<SelectTrigger id="cache-bucket">
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									{CACHE_BUCKET_OPTIONS.map((o) => (
										<SelectItem key={o.id} value={o.id}>
											{o.label}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>

						{targetMode === "none" ? null : (
							<div className="space-y-2">
								<Label className="block leading-snug" htmlFor="cache-target">
									Target{" "}
									<span className="font-normal text-muted-foreground">
										(optional — leave empty to clear the whole bucket)
									</span>
								</Label>
								{targetMode === "workspace" ? (
									<Select onValueChange={setTarget} value={target}>
										<SelectTrigger id="cache-target">
											<SelectValue placeholder="All workspaces (entire bucket)" />
										</SelectTrigger>
										<SelectContent>
											{workspaces.map((w) => (
												<SelectItem key={w.id} value={w.id}>
													{w.name}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								) : (
									<Input
										id="cache-target"
										onChange={(e) => setTarget(e.target.value)}
										placeholder={
											targetMode === "asset"
												? "asset key, e.g. asset:…--channel--…"
												: "email or firebase uid"
										}
										value={target}
									/>
								)}
							</div>
						)}

						<div className="space-y-2">
							<Label className="block leading-snug" htmlFor="workspace-scope">
								Workspace scope{" "}
								<span className="font-normal text-muted-foreground">
									{targetMode === "none"
										? "(applies to Clear and Warm)"
										: "(applies to Clear and Warm; a Target above overrides it for Clear)"}
								</span>
							</Label>
							<Select
								onValueChange={scopes.setWorkspace}
								value={scopes.workspaceScope}
							>
								<SelectTrigger id="workspace-scope">
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value={SCOPE_ALL}>All workspaces</SelectItem>
									{workspaces.map((w) => (
										<SelectItem key={w.id} value={w.id}>
											{w.name}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>

						{/* One Organisation, one operation: every cache key
						    opens with the owning Organisation, so this scope reaches every
						    workspace the Organisation owns without listing them. Exclusive
						    with the workspace scope above. */}
						<div className="space-y-2">
							<Label
								className="block leading-snug"
								htmlFor="organization-scope"
							>
								Organisation scope{" "}
								<span className="font-normal text-muted-foreground">
									(every workspace the Organisation owns, in one operation;
									replaces the workspace scope)
								</span>
							</Label>
							<Select
								onValueChange={scopes.setOrganization}
								value={scopes.organizationScope}
							>
								<SelectTrigger id="organization-scope">
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value={SCOPE_ALL}>All Organisations</SelectItem>
									{organizations.map((o) => (
										<SelectItem key={o.id} value={o.id}>
											{organizationOptionLabel(o)}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>

						<AlertDialog onOpenChange={setConfirmOpen} open={confirmOpen}>
							<AlertDialogTrigger asChild>
								<Button
									className="w-full"
									disabled={submitting || warming}
									variant="destructive"
								>
									{submitting ? "Clearing…" : "Clear cache"}
								</Button>
							</AlertDialogTrigger>
							<AlertDialogContent>
								<AlertDialogHeader>
									<AlertDialogTitle>Clear cache?</AlertDialogTitle>
									<AlertDialogDescription>
										{confirmDescription}
									</AlertDialogDescription>
								</AlertDialogHeader>
								<AlertDialogFooter>
									<AlertDialogCancel>Cancel</AlertDialogCancel>
									<AlertDialogAction onClick={handleConfirm}>
										Clear
									</AlertDialogAction>
								</AlertDialogFooter>
							</AlertDialogContent>
						</AlertDialog>

						{/* Warm honors the Bucket, Workspace scope and Organisation scope
						    selects (but not the clear-specific Target input). Additive and
						    idempotent (already-warm keys are skipped), so no confirmation
						    gate. */}
						<Button
							className="w-full"
							disabled={submitting || warming}
							onClick={handleWarm}
							variant="secondary"
						>
							{warming ? "Warming… (can take a few minutes)" : "Warm cache"}
						</Button>
					</div>
				</CardContent>
			</Card>
		</div>
	);
}

type CpuProfileResponse = {
	durationMs: number;
	sampleIntervalMs: number;
	sampleCount: number;
	distinctStacks: number;
	truncatedStacks: number;
	folded: string;
	topFunctions: Array<{
		frame: string;
		selfSamples: number;
		selfPct: number;
		totalSamples: number;
		totalPct: number;
	}>;
};

const PROFILE_DURATION_OPTIONS = [
	{ id: "15000", label: "15 seconds" },
	{ id: "30000", label: "30 seconds" },
	{ id: "60000", label: "60 seconds" },
] as const;

// Slack on top of the capture window for RPC transport + server aggregation.
const PROFILE_RPC_TIMEOUT_SLACK_MS = 30 * 1000;

function CpuProfileSection() {
	const [durationMs, setDurationMs] = useState<string>("15000");
	const [capturing, setCapturing] = useState(false);
	const [lastResult, setLastResult] = useState<CpuProfileResponse | null>(null);

	async function handleCapture() {
		setCapturing(true);
		try {
			const result = await ws.sendWithPromise<CpuProfileResponse>({
				id: ulid(),
				procedure: "instanceAdmin.captureCpuProfile",
				data: { durationMs: Number(durationMs) },
				timeoutMs: Number(durationMs) + PROFILE_RPC_TIMEOUT_SLACK_MS,
			});
			setLastResult(result);

			// Hand the collapsed stacks straight to the browser as a download. The
			// file drops into speedscope.app / flamegraph.pl as-is.
			const blob = new Blob([result.folded], { type: "text/plain" });
			const url = URL.createObjectURL(blob);
			const anchor = document.createElement("a");
			anchor.href = url;
			anchor.download = `cpu-profile-${new Date()
				.toISOString()
				.replaceAll(":", "-")}.folded.txt`;
			anchor.click();
			URL.revokeObjectURL(url);

			toast.success(
				`Captured ${result.sampleCount} samples (${result.distinctStacks} stacks) over ${result.durationMs / 1000}s`
			);
		} catch (err) {
			const message = err instanceof Error ? err.message : String(err);
			toast.error(`Capture failed: ${message}`);
		} finally {
			setCapturing(false);
		}
	}

	return (
		<Card className="w-full max-w-lg">
			<CardHeader>
				<CardTitle>
					<h2>CPU profile</h2>
				</CardTitle>
				<CardDescription>
					Sample the server's CPU for a window and download the stacks (open the
					file in speedscope.app). Negligible overhead while sampling; the first
					capture leaves the sampler running (~1% of a core) until the server
					restarts.
				</CardDescription>
			</CardHeader>
			<CardContent className="space-y-4">
				<div className="flex items-end gap-3">
					<div className="space-y-1">
						<Label htmlFor="cpu-profile-duration">Duration</Label>
						<Select
							disabled={capturing}
							onValueChange={setDurationMs}
							value={durationMs}
						>
							<SelectTrigger className="w-40" id="cpu-profile-duration">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								{PROFILE_DURATION_OPTIONS.map((option) => (
									<SelectItem key={option.id} value={option.id}>
										{option.label}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
					<Button disabled={capturing} onClick={handleCapture}>
						{capturing
							? `Capturing ${Number(durationMs) / 1000}s...`
							: "Capture"}
					</Button>
				</div>
				{lastResult && lastResult.topFunctions.length > 0 && (
					<div>
						<div className="mb-1 font-medium text-muted-foreground text-xs uppercase tracking-wide">
							Top functions by self time
						</div>
						<dl className="space-y-1 text-sm">
							{lastResult.topFunctions.slice(0, 8).map((fn) => (
								<div className="flex justify-between gap-4" key={fn.frame}>
									<dt className="truncate font-mono text-xs" title={fn.frame}>
										{fn.frame}
									</dt>
									<dd className="shrink-0 font-mono text-xs tabular-nums">
										{fn.selfPct}%
									</dd>
								</div>
							))}
						</dl>
					</div>
				)}
			</CardContent>
		</Card>
	);
}
